import { ref, shallowRef, watch } from 'vue';
import { defineStore } from 'pinia';
import { useRouter } from 'vue-router';
import moment from 'moment';

import { secondsToTime } from '../utils/helpers';
import { useNotifications } from './notifications.store';
import { useOnboardingChecklist } from './onboarding-checklist.store';
import { setAuthState } from '../composables/auth';
import { useIsMobile } from '../composables/isMobile';
import { update } from '../api/profile';

import { INTERVIEW_ROUNDS_ORDER } from '../storage';

import interviewsApi from '../api/interview-ai';

const { getUser, setUser } = setAuthState();
const { isMobile } = useIsMobile();

export const useInterviewsStore = defineStore('interviews', () => {
	const router = useRouter();
	const user = getUser();
	const onboardingChecklistStore = useOnboardingChecklist();
	const generalScore = ref({});
	const isActivitySidebarVisible = ref(!isMobile.value); 

	let lastRoundWithFeedback = null;
	let totalScore = 0;
	let totalProcessed = 0;
	let nonVerbals = 0;
	let speech = 0;
	let content = 0;
    
	const interviews = shallowRef([]);
	const completedInterviews = ref([]);
	const uncompletedInterviews = ref([]);
	const archivedInterviews = ref([]);
	const loading = shallowRef(false);
	const loaded = shallowRef(false);
    
	const showResultsModal = shallowRef(false);
	const roundResults = ref(null);
	const selectedRound = ref(null);
    
	const setShowResultsModal = (round) => {
		selectedRound.value = round;
		showResultsModal.value = true;
		onboardingChecklistStore.markItemAsCompleted(5,'reviewInterviewScore');
	};
    
	const hideResultsModal = () => {
		showResultsModal.value = false;
	};

	const createInterviewLoading = ref(false);

	const notificationsStore = useNotifications();

	const toggleActivitySidebarVisibility = () => {
		isActivitySidebarVisible.value = !isActivitySidebarVisible.value;
	};
    
	const createNextRoundHandler = async (interviewId, round) => {
		const response = await interviewsApi.createNextRound(interviewId, round);

		const interviewIndex = interviews.value.findIndex((interview) => {
			return interview?.uuid === interviewId;
		});

		if (interviewIndex === -1) {
			throw new Error('An error occurred while creating new interview round!');
		}

		await router.push({
			name: 'interview-ai-interview',
			params: {
				interviewId,
				roundId: response?.interview_rounds[response?.interview_rounds.length - 1]?.uuid
			}
		});
	};
    
	const getNextRoundAfter = (lastRound) => {
		if (lastRound?.round) {
			const lastRoundOrder = INTERVIEW_ROUNDS_ORDER.findIndex((r) => {
				return r.key === lastRound.round;
			});

			if (lastRoundOrder != null && lastRoundOrder + 1 <= INTERVIEW_ROUNDS_ORDER.length) {
				return INTERVIEW_ROUNDS_ORDER[lastRoundOrder + 1];
			}
		}
        
		return null;
	};

	const addTimeToInterview = (interview) => {
		const isFullInterview = !!interview?.is_full_interview;
		const interviewRounds = interview?.interview_rounds || [];
        
		const result = { 
			...interview,
			interview_rounds: interviewRounds.map((round) => {
				const totalQuestionsCount = round?.questions?.length || 0;
				const interviewLength = totalQuestionsCount * 4;

				const completedQuestionsCount = round?.questions?.reduce((total, q) => {
					if (q?.transcription !== null || q.skipped === true ) {
						return total + 1;
					}

					return total;
				}, 0);

				return {
					...round,
					time: `${interviewLength} min`,
					progress: (completedQuestionsCount / totalQuestionsCount) * 100
				};
			})
		};
        
		if (!isFullInterview) {
			const nextRound = getNextRoundAfter(interviewRounds.at(-1));
            
			if (nextRound != null) {
				return {
					...result,
					nextRound
				};
			}
		}
        
		return result;
	};

	const addTimeToOldInterview = (interview) => {
		const totalQuestionsCount = interview.questions?.length;
		const interviewLength = totalQuestionsCount * 4;

		const completedQuestionsCount = interview.questions?.reduce((total, q) => {
			if (q?.transcription != null || q.skipped == true ) {
				return total + 1;
			}

			return total;
		}, 0);

		return {
			...interview,
			time: `${interviewLength} min`,
			progress: (completedQuestionsCount / totalQuestionsCount) * 100
		};
	};

	const markInterviewAsCompleted = (interviewId) => {
		const interviewIndex = interviews.value.findIndex((interview) => interview.uuid === interviewId);

		interviews.value.splice(interviewIndex, 1, {
			...interviews.value[interviewId],
			is_completed: true
		});
	};

	const fetchInterviews = async () => {
		loading.value = true;
		loaded.value = false;

		try {
			const userInterviews = await interviewsApi.getInterviews();
			interviews.value = userInterviews.map(addTimeToInterview);
		} catch (error) {
			notificationsStore.notify('Failed to get interviews!', 'error');
		} finally {
			loading.value = false;
			loaded.value = true;
		}
	};

	const createInterview = async (interviewData) => {
		createInterviewLoading.value = true;

		try {
			const newInterview = await interviewsApi.createInterview(
				interviewData
			);
            
			const interviewWithTime = addTimeToInterview(newInterview);

			interviews.value.push(interviewWithTime);

			if (!user.options?.free_interview_used) {
				const userResponse = await update({
					options: {
						...user.options,
						free_interview_used: true
					}
				});

				setUser(userResponse.data.data);
			}

			return interviewWithTime;
		} catch (error) {
			notificationsStore.notify('Failed to create new interview!', 'error');
		} finally {
			createInterviewLoading.value = false;
		}
	};

	const retakeRoundHandler = async (interviewId, roundId) => {
		try {
			await interviewsApi.retakeRound(roundId);

			router.push({
				name: 'interview-ai-interview',
				params: { 
					interviewId,
					roundId
				} 
			});
		} catch (error) {
			notificationsStore.notify('Failed to retake interview!', 'error');
		}
	};

	const deleteInterviewHandler = async (interviewId) => {
		const interviewIndex = interviews.value.findIndex(
			(interview) => interview.uuid === interviewId
		);

		await interviewsApi.deleteInterview(interviewId);
		const interviewsCopy = interviews.value;
		interviews.value = null;

		interviewsCopy.splice(interviewIndex, 1);
		interviews.value = interviewsCopy;
	};

	const renameInterviewHandler = async (interviewId, newName) => {
		const interviewIndex = interviews.value.findIndex(
			(interview) => interview.uuid === interviewId
		);

		const updatedInterview = await interviewsApi.updateInterview(interviewId, {
			title: newName
		});

		interviews.value.splice(interviewIndex, 1, addTimeToInterview(updatedInterview));
	};

	const validQuestionFilter = (question) => question?.transcription != null;

	const getTranscriptionForRound = (round) => {
		const roundQuestions = round?.questions || [];
		const transcription = [];

		const pushToTranscription = (question) => {
			transcription.push({
				author: 'Interviewer',
				time: secondsToTime(parseInt(question.start_at)),
				text: question.title
			});

			transcription.push({
				author: 'You',
				time: secondsToTime(parseInt(question.end_at)),
				text: question.transcription,
				videoLink: question.meta?.videoMediaName,
				audioLink: question.meta?.audioMediaName
			});
		};

		roundQuestions
			.filter(validQuestionFilter)
			.forEach((question) => {
				pushToTranscription(question);
    
				question?.childrens
					?.filter(validQuestionFilter)
					.forEach((subq) => {
						pushToTranscription(subq);
					});
			});

		return transcription;
	};
    
	const computeInterviewProps = (interview) => {
		const interview_rounds = interview?.interview_rounds.map((round) => {
			const isProcessedPartially = round.meta?.contentResults?.score != null 
                && round.meta?.speechResults?.score != null;
			const isProcessed = round.meta?.speechResults?.score != null 
                && round.meta?.contentResults?.score != null 
                && round.meta?.nonVerbalResults?.score != null;
			const roundCopy = {
				...round,
				isProcessed,
				isProcessedPartially
			};
			let roundScore = 0;

			if (isProcessedPartially) {
				roundCopy.interviewTranscription = getTranscriptionForRound(round);
			}

			if (isProcessed && round?.is_completed) {
				const stats = round.meta;
				roundScore = Math.ceil(
					(
						parseInt(stats?.speechResults?.score) + 
                        parseInt(stats?.contentResults?.score || 0) + 
                        parseInt(stats?.nonVerbalResults?.score || 0 )
					)
                    / 3
				);
                
				totalProcessed++;

				totalScore += roundScore;
				nonVerbals += parseInt(stats.nonVerbalResults.score);
				speech += parseInt(stats.speechResults.score);
				content += parseInt(stats.contentResults.score);
                
				if (lastRoundWithFeedback != null) {
					const currentRoundDate = moment(round?.updated_at);
					const savedRoundDate = moment(lastRoundWithFeedback.updated_at);
                    
					if (currentRoundDate.isAfter(savedRoundDate)) {
						lastRoundWithFeedback = round;
					}
				} else {
					lastRoundWithFeedback = round;
				}
			}

			return {
				...roundCopy,
				totalScore: roundScore
			};
		});

		return {
			...interview,
			interview_rounds
		};
	};
    
	watch(interviews, (newValue) => {
		const completed = [];
		const uncompleted = [];
		const archived = [];

		// TODO: imbricated loops, needs to be fixed on the backend side
		newValue.forEach((interview) => {
			const completedRoundsCount = interview?.interview_rounds.filter((round) => round?.is_completed)?.length;

			if (interview?.is_old_interview) {
				archived.push(
					addTimeToOldInterview({
						...interview,
						questions: [],
						interviewTranscription: getTranscriptionForRound(interview)
					})
				);
			} else if (completedRoundsCount === interview?.interview_rounds.length) {
				completed.push(computeInterviewProps({
					...interview,
					interviewTranscription: getTranscriptionForRound(interview)
				}));
			} else {
				uncompleted.push({
					...interview,
					interviewTranscription: getTranscriptionForRound(interview)
				});
			}
		});
        
		completedInterviews.value = completed;
		uncompletedInterviews.value = uncompleted;
		archivedInterviews.value = archived;

		generalScore.value = {
			totalScore: Math.round(totalScore/totalProcessed) || 0,
			nonVerbals: Math.round(nonVerbals/totalProcessed) || 0,
			speech: Math.round(speech/totalProcessed) || 0,
			content: Math.round(content/totalProcessed) || 0,
			mostRecentFeedback: lastRoundWithFeedback?.meta
		};
	});
    
	const getInterviewWithId = (id) => interviews.value.find((interview) => interview.id === id);

	return {
		interviews,
		completedInterviews,
		uncompletedInterviews,
		archivedInterviews,
		generalScore,
		roundResults,
		selectedRound,
		showResultsModal,
		isActivitySidebarVisible,

		fetchInterviews,
		createInterview,
		deleteInterviewHandler,
		retakeRoundHandler,
		renameInterviewHandler,
		markInterviewAsCompleted,
		createNextRoundHandler,
		setShowResultsModal,
		hideResultsModal,

		getInterviewWithId,
		toggleActivitySidebarVisibility,

		loading,
		createInterviewLoading,
		loaded,
	};
});
